'use client';

import { useState } from 'react';
import Icon from '@/components/ui/AppIcon';

// All localStorage keys managed by the session system
const SESSION_KEYS = [
  'session_token',
  'session_code_type',
  'session_active',
];

function clearAllSession() {
  SESSION_KEYS.forEach(k => localStorage.removeItem(k));
}

export default function AccessCodeForm() {
  const [code, setCode] = useState('');
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const val = e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, '').slice(0, 8);
    setCode(val);
    if (error) setError('');
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (code.length < 8) {
      setError('Der Zugangscode muss 8 Zeichen lang sein.');
      return;
    }
    setIsLoading(true);

    clearAllSession();

    try {
      // Validate via server-side API route — codes are never sent back to the browser
      const response = await fetch('/api/validate-code', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ code }),
      });

      const result = await response.json();

      if (!result.valid) {
        setError(result.message || 'Ungültiger oder abgelaufener Zugangscode. Überprüfe deine Instagram-DM.');
        setIsLoading(false);
        return;
      }

      // Store code type so the UI can show admin-specific elements
      localStorage.setItem('session_code_type', result.codeType);
      // Store the session token (NOT the actual code) — used for server-side validation
      localStorage.setItem('session_token', result.sessionToken);
      // Mark session as active
      localStorage.setItem('session_active', 'true');

      // Store whether this code has a timer (for onboarding display)
      localStorage.setItem('session_has_timer', result.sessionDurationMinutes ? '1' : '0');

      // Store VIP flag
      if (result.isVip || result.codeType === 'vip') {
        localStorage.setItem('session_is_vip', '1');
      } else {
        localStorage.removeItem('session_is_vip');
      }

      // Pass onboarding_done from server to the map page via a session flag.
      // No more localStorage-based onboarding tracking — the DB is the source of truth.
      if (!result.onboardingDone) {
        localStorage.setItem('session_show_onboarding', '1');
      } else {
        localStorage.removeItem('session_show_onboarding');
      }

      setIsSuccess(true);
      setTimeout(() => {
        if (result.codeType === 'admin') {
          window.location.href = '/admin-panel';
        } else {
          window.location.href = '/interactive-map';
        }
      }, 800);
    } catch {
      setError('Fehler beim Überprüfen des Codes. Bitte versuche es erneut.');
      setIsLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} noValidate className="space-y-6">
      {/* Input */}
      <div>
        <label
          htmlFor="access-code"
          className="block font-mono text-xs text-muted-foreground uppercase tracking-widest mb-2"
        >
          Zugangscode
        </label>
        <div className="relative">
          <input
            id="access-code"
            type="text"
            value={code}
            onChange={handleChange}
            placeholder="XXXXXXXX"
            maxLength={8}
            disabled={isLoading || isSuccess}
            autoComplete="off"
            spellCheck={false}
            aria-label="8-stelliger Zugangscode"
            aria-describedby={error ? 'code-error' : 'code-hint'}
            className="w-full h-14 bg-background border-2 border-white/10 px-5 font-mono text-2xl text-foreground uppercase tracking-[0.4em] placeholder:text-muted-foreground/30 focus:border-primary focus:outline-none transition-all duration-[250ms] ease-[cubic-bezier(0.25,0.46,0.45,0.94)] disabled:opacity-50"
            style={{ borderRadius: '2px' }}
          />
          {/* Decorative corner accents */}
          <span className="absolute top-0 left-0 w-3 h-3 border-t-2 border-l-2 border-primary pointer-events-none" />
          <span className="absolute top-0 right-0 w-3 h-3 border-t-2 border-r-2 border-primary pointer-events-none" />
          <span className="absolute bottom-0 left-0 w-3 h-3 border-b-2 border-l-2 border-primary pointer-events-none" />
          <span className="absolute bottom-0 right-0 w-3 h-3 border-b-2 border-r-2 border-primary pointer-events-none" />
        </div>

        {/* Length indicator */}
        <div className="flex items-center justify-between mt-2">
          <div className="flex gap-1">
            {Array.from({ length: 8 }).map((_, i) => (
              <div
                key={i}
                className={`h-1 w-6 transition-all duration-[250ms] ${i < code.length ? 'bg-primary' : 'bg-white/10'}`}
                style={{ borderRadius: '1px' }}
              />
            ))}
          </div>
          <span className="font-mono text-xs text-muted-foreground">{code.length}/8</span>
        </div>

        {/* Error */}
        {error && (
          <div
            id="code-error"
            role="alert"
            className="mt-3 flex items-start gap-2 px-3 py-2 bg-error/10 border border-error/30"
            style={{ borderRadius: '2px' }}
          >
            <Icon name="ExclamationTriangleIcon" size={16} variant="solid" className="text-error flex-shrink-0 mt-0.5" />
            <p className="text-error text-sm font-body leading-snug">{error}</p>
          </div>
        )}

        {/* Hint */}
        {!error && (
          <p id="code-hint" className="mt-2 font-mono text-xs text-muted-foreground/60">
            8-stelliger alphanumerischer Code · Nur Großbuchstaben &amp; Ziffern
          </p>
        )}
      </div>

      {/* Submit button */}
      <button
        type="submit"
        disabled={isLoading || isSuccess || code.length < 8}
        className="w-full h-14 bg-primary text-white font-heading text-base uppercase tracking-widest shadow-lg hover:-translate-y-[3px] active:scale-[0.96] transition-all duration-[250ms] ease-[cubic-bezier(0.25,0.46,0.45,0.94)] disabled:opacity-40 disabled:cursor-not-allowed disabled:transform-none flex items-center justify-center gap-3"
        style={{ borderRadius: '2px' }}
      >
        {isLoading ? (
          <>
            <div
              className="w-5 h-5 border-2 border-white/30 border-t-white animate-spin"
              style={{ borderRadius: '50%' }}
            />
            <span>Wird geprüft...</span>
          </>
        ) : isSuccess ? (
          <>
            <Icon name="CheckCircleIcon" size={20} variant="solid" className="text-green-400" />
            <span>Zugang gewährt</span>
          </>
        ) : (
          <>
            <Icon name="LockOpenIcon" size={20} variant="outline" />
            <span>Zugang anfordern</span>
          </>
        )}
      </button>
    </form>
  );
}